import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import WordSearch from './WordSearch';
import Alert from '../common/Alert';
import styles from '@/components/Dictionary/Dictionary.module.less';
import { searchWord } from '../../libs/api';
import DictionaryResult from './DictionaryResult';
import { BookOutlined, RobotOutlined } from '@ant-design/icons';
import { DictionaryResponse } from '../../libs/types';
import { searchAITranslation } from '../../libs/api';
import LatelySearch from './LatelySearch';
import { formatEntry } from '@/utils/dictionary';

// interface SearchResult {
//   message: string;
//   result: {
//     m: string;
//     aiTranslation?: string;
//     examples?: string[];
//     weblio?: string;
//     goo?: string;
//     notFound: boolean;
//   };
// }

const Dictionary: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [query, setQuery] = useState('');
  const [showAlert, setShowAlert] = useState(false);
  const [dbResult, setDbResult] = useState<DictionaryResponse | null>(null);
  const [aiResult, setAiResult] = useState<DictionaryResponse | null>(null);
  const [loading, setLoading] = useState({ db: false, ai: false });
  const [aiRequested, setAiRequested] = useState(false);

  // 添加一个 ref 来跟踪是否已经执行过初始查询
  const initialQueryExecuted = React.useRef(false);

  // 添加防抖相关的状态
  const lastRefreshTime = React.useRef<number>(0);
  const [refreshCooldown, setRefreshCooldown] = useState<number>(0);

  const handleSearch = async (query: string) => {
    if (query.length > 30) {
      setShowAlert(true);
      return;
    }
    navigate(`/dictionary?query=${query}`);
    setLoading({ ...loading, db: true });
    setAiResult(null);
    setAiRequested(false);

    try {
      const dbData = await searchWord(query);
      setDbResult(dbData);

      // 如果基础词典没有找到结果，自动触发 AI 查询
      if (dbData && dbData.result && dbData.result.notFound) {
        setAiRequested(true); // 标记为已请求，防止重复请求
        setLoading(prev => ({ ...prev, ai: true }));
        try {
          const aiData = await searchAITranslation(query);
          setAiResult(aiData);
        } catch (error) {
          console.error('AI翻译失败:', error);
        } finally {
          setLoading(prev => ({ ...prev, ai: false }));
        }
      }
    } catch (error) {
      console.error('查询失败:', error);
    } finally {
      setLoading(prev => ({ ...prev, db: false }));
    }
  };

  const handleAITranslate = async () => {
    if (!query || aiRequested) return; // 如果已经请求过，直接返回

    setLoading(prev => ({ ...prev, ai: true }));
    try {
      const aiData = await searchAITranslation(query);
      setAiResult(aiData);
      setAiRequested(true);
    } catch (error) {
      console.error('AI翻译失败:', error);
    } finally {
      setLoading(prev => ({ ...prev, ai: false }));
    }
  };

  const handleRefreshAI = async () => {
    if (!query) return;

    const now = Date.now();
    const timeSinceLastRefresh = now - lastRefreshTime.current;
    const cooldownPeriod = 30 * 1000; // 30秒

    // 检查是否在冷却期内
    if (timeSinceLastRefresh < cooldownPeriod) {
      const remainingTime = Math.ceil((cooldownPeriod - timeSinceLastRefresh) / 1000);
      console.log(`请等待 ${remainingTime} 秒后再次刷新`);
      return;
    }

    setLoading(prev => ({ ...prev, ai: true }));
    try {
      const aiData = await searchAITranslation(query, true); // 强制刷新
      setAiResult(aiData);
      setAiRequested(true);

      // 更新最后刷新时间并开始倒计时
      lastRefreshTime.current = now;
      setRefreshCooldown(30);
    } catch (error) {
      console.error('AI翻译刷新失败:', error);
    } finally {
      setLoading(prev => ({ ...prev, ai: false }));
    }
  };

  // 处理点击相关词或模糊匹配词的事件
  const handleWordClick = (word: string) => {
    setQuery(word);
    handleSearch(word);
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const queryParam = params.get('query');
    if (queryParam) {
      setQuery(queryParam);
      // 只在首次加载且未执行过查询时执行
      if (!initialQueryExecuted.current) {
        handleSearch(queryParam);
        initialQueryExecuted.current = true;
      }
    }
  }, [location.search]); // 只依赖 location.search

  // 处理刷新冷却倒计时
  useEffect(() => {
    if (refreshCooldown > 0) {
      const timer = setTimeout(() => {
        setRefreshCooldown(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [refreshCooldown]);

  const handleAlertClose = () => {
    setShowAlert(false);
  };

  const handleAlertConfirm = () => {
    navigate(`/applications/text-analyze?text=${query}`);
  };

  const handleAddToWordbook = (content: string, source: string) => {
    // TODO: 实现添加到单词本的功能
    console.log('添加到单词本:', { content, source });
  };

  const formatDictionaryEntries = (
    dbResult: DictionaryResponse | null,
    aiResult: DictionaryResponse | null,
  ) => {
    const entries = [];

    // 基础词典结果 - 只在有结果时显示
    if (dbResult && dbResult.result && !dbResult.result.notFound) {
      entries.push({
        source: '基础词典 / Base Dictionary',
        content: formatEntry(dbResult.result.m),
        type: 'html' as const,
        isEmpty: false,
        icon: <BookOutlined className={styles.sourceIcon} />,
        sourceType: 'dictionary',
      });
    }

    // AI 翻译结果（包括未请求状态）
    const aiContent = aiResult?.result?.m ? formatEntry(aiResult.result.m) : '';
    entries.push({
      source: 'AI释义 / AI Explanation',
      content: aiContent,
      type: 'html' as const,
      isEmpty: !aiResult || !aiResult.result,
      icon: <RobotOutlined className={styles.sourceIcon} />,
      loading: loading.ai,
      onRequest: handleAITranslate,
      onRefresh: handleRefreshAI,
      requested: aiRequested,
      sourceType: 'ai',
      refreshCooldown: refreshCooldown,
    });

    return entries;
  };

  return (
    <div className="centerContent">
      <div className={styles.dictionaryContainer}>
        <h1 className={styles.title}>在线日语词典</h1>
        {/* <p className={styles.description}>输入单词，获取详细解释、例句、发音等信息，支持日语，中文和英语。</p> */}
        <div className={styles.searchSection}>
          <WordSearch onSearch={handleSearch} initialQuery={query} />
        </div>

        {(dbResult || aiResult) && (
          <DictionaryResult
            entries={formatDictionaryEntries(dbResult, aiResult)}
            loading={loading.db || loading.ai}
            onAddToWordbook={handleAddToWordbook}
            searchWord={query}
            relatedWords={dbResult?.result.relatedWords}
            fuzzyMatches={dbResult?.result.fuzzyMatches}
            onWordClick={handleWordClick}
          />
        )}
        {/* 最近搜索历史 */}
        <div className={styles.latelySearchWrapper}>
          <LatelySearch
            title="最新查词 / Dictionary Lookup"
            type="recent"
            dataSource="dictionary"
            onWordClick={handleWordClick}
          />
          <LatelySearch title="AI翻译热词 / AI Translation Trending" type="trending" dataSource="ai" onWordClick={handleWordClick} />
        </div>
        {showAlert && (
          <Alert
            message="输入内容过长"
            description="您输入的内容超过30个字符，是否需要使用文本分析工具？"
            type="warning"
            onClose={handleAlertClose}
            onConfirm={handleAlertConfirm}
            confirmText="使用文本分析"
          />
        )}
      </div>
    </div>
  );
};

export default Dictionary;
